import React from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';

import { Typography } from '../ui';
import type { WeekDate } from '../../utils/weekAgenda';

import { accentTextOn } from '../../styles/accentText';
type LivesWeekStripProps = {
  accentColor: string;
  dayNames: string[];
  hasEvents: (dateISO: string) => boolean;
  isDark: boolean;
  isMobile: boolean;
  language: string;
  onSelectDay: (dateISO: string | null) => void;
  /** null — режим «Вся неделя». */
  selectedDay: string | null;
  themeColors: {
    textPrimary: string;
    textSecondary: string;
  };
  todayISO: string;
  weekDates: WeekDate[];
};

/**
 * CORE-1134 — компактная лента семи дат под шапкой афиши: слева кнопка
 * «Вся неделя», справа семь равных ячеек. Сегодняшний день подсвечен
 * акцентом, прошедшие — приглушены, выбранный день (или «Вся неделя») —
 * с акцентной рамкой и лёгкой заливкой. Ячейки делят ширину поровну и не
 * создают горизонтального скролла: на телефоне ужимаются до ~40px.
 */
export const LivesWeekStrip: React.FC<LivesWeekStripProps> = ({
  accentColor,
  dayNames,
  hasEvents,
  isDark,
  isMobile,
  language,
  onSelectDay,
  selectedDay,
  themeColors,
  todayISO,
  weekDates,
}) => {
  const isWeekMode = selectedDay === null;
  const idleBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const idleBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.55)';
  const activeBg = isDark ? 'rgba(233,130,69,0.14)' : 'rgba(255,241,232,0.95)';
  const cellRadius = isMobile ? 12 : 14;

  const cellShell = (active: boolean) => ({
    borderRadius: cellRadius,
    borderWidth: 1,
    borderColor: active ? accentColor : idleBorder,
    backgroundColor: active ? activeBg : idleBg,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' } as any }),
  });

  return (
    <View
      testID="lives-week-strip"
      style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'stretch',
        gap: isMobile ? 8 : 12,
        paddingHorizontal: isMobile ? 12 : 20,
        paddingTop: 4,
        paddingBottom: isMobile ? 14 : 18,
        borderBottomWidth: 1,
        borderBottomColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      }}
    >
      <TouchableOpacity
        testID="lives-week-all"
        accessibilityRole="button"
        accessibilityState={{ selected: isWeekMode }}
        activeOpacity={0.8}
        onPress={() => onSelectDay(null)}
        style={[
          cellShell(isWeekMode),
          isMobile
            ? { height: 40, alignSelf: 'stretch' }
            : { width: 136, minHeight: 56 },
        ]}
      >
        <Typography
          variant="overline"
          style={{ color: isWeekMode ? accentTextOn(accentColor) : themeColors.textPrimary }}
        >
          {language === 'ru' ? 'Вся неделя' : 'Whole week'}
        </Typography>
      </TouchableOpacity>

      <View style={{ flex: 1, flexDirection: 'row', gap: isMobile ? 4 : 8, minWidth: 0 }}>
        {weekDates.map(({ dateISO, day, year, month }) => {
          const isSelected = selectedDay === dateISO;
          const isToday = dateISO === todayISO;
          const isPast = dateISO < todayISO;
          const weekday = dayNames[new Date(year, month, day).getDay()];
          const withEvents = hasEvents(dateISO);
          // CORE-1733 — подпись дня — текст: акцентный токен, а не фирменный оранжевый рамки.
          const textColor = isSelected || isToday ? accentTextOn(accentColor) : themeColors.textPrimary;
          const nameColor = isSelected || isToday ? accentTextOn(accentColor) : themeColors.textSecondary;
          return (
            <TouchableOpacity
              key={dateISO}
              testID={`lives-week-day-${dateISO}`}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={`${weekday} ${day}`}
              activeOpacity={0.8}
              onPress={() => onSelectDay(isSelected ? null : dateISO)}
              style={[
                cellShell(isSelected),
                {
                  flex: 1,
                  minWidth: 0,
                  minHeight: isMobile ? 52 : 56,
                  paddingVertical: isMobile ? 6 : 8,
                  opacity: isPast && !isSelected ? 0.45 : 1,
                },
                // Сегодня без выбора — тонкая акцентная рамка, чтобы отличать
                // «сегодня» от «выбрано» (у выбранного ещё и заливка).
                isToday && !isSelected && { borderColor: isDark ? 'rgba(233,130,69,0.55)' : 'rgba(233,130,69,0.6)' },
              ]}
            >
              <Typography variant="caption" numberOfLines={1} style={{ color: nameColor }}>
                {weekday}
              </Typography>
              <Typography variant="overline" numberOfLines={1} style={{ color: textColor }}>
                {String(day)}
              </Typography>
              <View
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  marginTop: 3,
                  backgroundColor: withEvents
                    ? (isSelected || isToday ? accentColor : (isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'))
                    : 'transparent',
                }}
              />
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};
